/**
 * CreateTemplateDialog Component
 *
 * shadcn Dialog for creating a new email template.
 * Auto-generates templateKey from name. Pre-fills folder from current selection.
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import { FolderSelect } from './FolderSelect'
import type { EmailFolder } from '@/lib/email-templates/folder-types'
import type { EmailTemplateCreate } from '@/lib/email-templates'

/** Outlook-compatible starter HTML template */
const STARTER_HTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>EQuIP Email</title>
</head>
<body style="margin: 0; padding: 0; background-color: #ffffff; font-family: Arial, Helvetica, sans-serif;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f5f5f5;">
<tr>
<td align="center" style="padding: 20px 0;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="background-color: #ffffff; border-radius: 8px;">
<tr>
<td style="background-color: #053252; padding: 24px 32px; border-radius: 8px 8px 0 0;">
<h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 600;">EQuIP Update</h1>
</td>
</tr>
<tr>
<td style="padding: 32px; background-color: #ffffff;">
<p style="margin: 0 0 16px 0; color: #333333; font-size: 16px; line-height: 1.5; background-color: transparent;">Hello,</p>
<p style="margin: 0 0 16px 0; color: #333333; font-size: 16px; line-height: 1.5; background-color: transparent;">Your content here.</p>
</td>
</tr>
<tr>
<td style="padding: 24px 32px; background-color: #f5f5f5; border-radius: 0 0 8px 8px;">
<p style="margin: 0; color: #666666; font-size: 12px; line-height: 1.4; background-color: transparent;">Eventus Whole Health &mdash; EQuIP Program</p>
</td>
</tr>
</table>
</td>
</tr>
</table>
</body>
</html>`

function generateTemplateKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

interface CreateTemplateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  folders: EmailFolder[]
  defaultFolderId?: number | null
  onSubmit: (data: EmailTemplateCreate) => Promise<void>
  isSubmitting: boolean
}

export function CreateTemplateDialog({
  open,
  onOpenChange,
  folders,
  defaultFolderId,
  onSubmit,
  isSubmitting,
}: CreateTemplateDialogProps) {
  const [name, setName] = useState('')
  const [templateKey, setTemplateKey] = useState('')
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false)
  const [subject, setSubject] = useState('')
  const [category, setCategory] = useState('announcements')
  const [audience, setAudience] = useState('physician')
  const [folderId, setFolderId] = useState<number | null>(defaultFolderId ?? null)
  const [description, setDescription] = useState('')
  const [keyError, setKeyError] = useState<string | null>(null)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName('')
      setTemplateKey('')
      setKeyManuallyEdited(false)
      setSubject('')
      setCategory('announcements')
      setAudience('physician')
      setFolderId(defaultFolderId ?? null)
      setDescription('')
      setKeyError(null)
    }
  }, [open, defaultFolderId])

  // Auto-generate key from name (unless manually edited)
  useEffect(() => {
    if (!keyManuallyEdited && name) {
      setTemplateKey(generateTemplateKey(name))
    }
  }, [name, keyManuallyEdited])

  const handleKeyChange = (value: string) => {
    setKeyManuallyEdited(true)
    setTemplateKey(value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
    setKeyError(null)
  }

  const isValid = name.trim() && templateKey.trim() && subject.trim()

  const handleSubmit = async () => {
    if (!isValid) return

    const keyPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
    if (!keyPattern.test(templateKey)) {
      setKeyError('Must be lowercase letters, numbers, and hyphens only')
      return
    }

    await onSubmit({
      templateKey,
      name: name.trim(),
      category,
      audience,
      subject: subject.trim(),
      description: description.trim() || undefined,
      htmlContent: STARTER_HTML,
      folderId,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Template</DialogTitle>
          <DialogDescription>
            Create a new email template. It will open in the editor after creation.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-4 py-4">
          {/* Name */}
          <div className="col-span-12 space-y-2">
            <Label htmlFor="template-name">Name</Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q2 2026 Physician Reminder"
              className="rounded-lg"
            />
          </div>

          {/* Template Key */}
          <div className="col-span-12 space-y-2">
            <Label htmlFor="template-key">Template Key</Label>
            <Input
              id="template-key"
              value={templateKey}
              onChange={(e) => handleKeyChange(e.target.value)}
              placeholder="q2-2026-physician-reminder"
              className="rounded-lg bg-muted"
            />
            {keyError && (
              <p className="text-xs text-destructive">{keyError}</p>
            )}
          </div>

          {/* Subject */}
          <div className="col-span-12 space-y-2">
            <Label htmlFor="template-subject">Subject Line</Label>
            <Input
              id="template-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="EQuIP Q2 2026 Reminder"
              className="rounded-lg"
            />
          </div>

          {/* Category */}
          <div className="col-span-6 space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="announcements">Announcements</SelectItem>
                <SelectItem value="reminders">Reminders</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="notifications">Notifications</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Audience */}
          <div className="col-span-6 space-y-2">
            <Label>Audience</Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger className="rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="physician">Physician</SelectItem>
                <SelectItem value="non-physician">Non-Physician</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Folder */}
          <div className="col-span-12 space-y-2">
            <Label>Folder</Label>
            <FolderSelect
              folders={folders}
              value={folderId}
              onChange={setFolderId}
            />
          </div>

          {/* Description */}
          <div className="col-span-12 space-y-2">
            <Label htmlFor="template-description">Description (optional)</Label>
            <Textarea
              id="template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this template's purpose..."
              className="rounded-lg resize-none"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Template'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
